import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { formatValue } from '../../base/common/utils';
import { session } from '../../../services/session';
import { dateService } from '../../../services/date';
import { Loader } from '../../misc/loader-utils';
import breadCrumbArrow from '../../../images/icons/breadcrumb-arrow.svg';
import { ratesService } from './rates-service';

import Flag from '../../base/flag/flag';

import Product from '../trends/cart-explorer/product';
import RetailerOverviewDropdown from './retailer-overview-base-dropdown';
import StrategyBio from './strategy-bio';
import SplitSortDropdown from '../../base/dropdowns/split-sort-dropdown';
import SearchBar from './search-bar';
import Pagination from '../../base/common/pagination';
import ReportDropdown from '../../base/dropdowns/report-dropdown';

import './styles/strategy-products.scss';

const presetDateRanges = dateService.getPresetDateRanges();
const range = 'thirtyDay';
const dateRange = {
  ...presetDateRanges[range],
  name: range,
  unit: 'day',
};

// SortBy Dropdown Options
const sortOptions = [
  { label: 'EPC', value: 'publisher_epc' },
  { label: 'Price', value: 'product_price' },
  { label: 'Clicks', value: 'clicks' },
  { label: 'Earnings', value: 'publisher_total_earnings' },
];

const availabilityOptions = [{ label: 'In Stock Only', value: true }];

const performanceMap = {
  publisher_epc: 'EPC',
  clicks: 'Clicks',
  publisher_total_earnings: 'Earnings',
};

const StrategyProducts = ({ match }) => {
  const [didMount, setDidMount] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showBio, setShowBio] = useState(true);
  const [mockData, setMockData] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [products, setProducts] = useState(null);
  const [sortBy, setSortBy] = useState(sortOptions[3]);
  const [direction, setDirection] = useState('asc');
  const [availability, setAvailability] = useState(availabilityOptions);
  const [params, setParams] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(null);

  const productVisibility = products && products.length;

  // For report dropdown to create report for all items
  const reportOptionsOverride = [{ label: 'All Rows', value: 'all' }];
  const rowCounts = { visible: 0, all: totalItems };

  useEffect(() => {
    setDidMount(true);
  }, []);

  useEffect(() => {
    setParams(prevState => {
      return {
        ...prevState,
        date_from: dateService.formatDate(dateRange.startDate),
        date_to: dateService.formatDate(dateRange.endDate),
        merch_id: match.params.retailerId,
        strategy_id: match.params.strategyId,
        page: prevState && prevState.per_page !== perPage ? 1 : page,
        per_page: perPage,
        ...(didMount && {
          order_by: sortBy.value,
          direction,
          is_in_stock: getSelectedAvailability(),
        }),
      };
    });
  }, [sortBy, direction, availability, page, perPage]);

  useEffect(() => {
    if (didMount) {
      setLoading(true);
      getTableData();
    }
  }, [params, mockData]);

  useEffect(() => {
    if (didMount && productQuery) {
      setParams(prevState => {
        return {
          ...prevState,
          product_query: productQuery,
        };
      });
    } else {
      clearParams();
    }
  }, [productQuery]);

  function getTableData() {
    if (mockData) {
      const mockProductsResponse = ratesService.getMockStrategyProducts();
      const mockStrategy = ratesService.getMockStrategies()[0];
      setProducts(processProducts(mockProductsResponse.stats));
      setTotalItems(mockProductsResponse.page_info.total_items);
      setStrategy(processStrategy(mockStrategy));
      setLoading(false);
    } else if (params.order_by || params.product_query || params.is_in_stock) {
      ratesService.getStrategyProducts(params, match.params.strategyId).then(resp => {
        setProducts(processProducts(resp.data.data[0].stats));
        setTotalItems(resp.data.data[0].page_info.total_items);
        setLoading(false);
      });
    } else {
      Promise.all([
        ratesService.getStrategyProducts(params, match.params.strategyId),
        ratesService.getStrategies(params),
      ])
        .then(([products, strategies]) => {
          setProducts(processProducts(products.data.data[0].stats));
          setTotalItems(products.data.data[0].page_info.total_items);
          const strategyStats = strategies.data.data[0].stats;
          setStrategy(processStrategy(strategyStats[0]));
          setLoading(false);
        })
        .catch(() => {
          setStrategy(null);
          setProducts(null);
          setLoading(false);
        });
    }
  }

  function processProducts(products) {
    return products.map(product => {
      return {
        ...product,
        feed_price_usd: product.product_price,
      };
    });
  }

  function processStrategy(strategy) {
    return {
      ...strategy,
      clicks: formatValue(strategy.clicks),
      publisher_epc: formatValue(strategy.publisher_epc, 'currency'),
      publisher_total_earnings: formatValue(
        strategy.publisher_total_earnings,
        'currency',
        'publisher_total_earnings',
      ),
    };
  }

  function clearParams() {
    const newParams = { ...params };
    if (newParams.product_query) {
      delete newParams.product_query;
      setParams(newParams);
    }
  }

  function getSelectedAvailability() {
    const availabilitySelected = availability.reduce((acc, option) => {
      return option.selected ? [...acc, option.value] : acc;
    }, []);
    return availabilitySelected.length ? availabilitySelected[0] : null;
  }

  const productItems = !loading ? (
    products && products.length ? (
      products.map((product, idx) => (
        <Product key={idx} product={product} type="strategy-product" />
      ))
    ) : (
      <div className="no-results">No products found for this strategy at this time.</div>
    )
  ) : null;

  const sortByDropdown = productVisibility ? (
    <SplitSortDropdown
      sortOptions={sortOptions}
      sortBy={sortBy}
      setSortBy={setSortBy}
      parentDirection={direction}
      setParentDirection={setDirection}
    />
  ) : null;

  const performance = strategy
    ? Object.keys(performanceMap).map((key, index) => {
        return (
          <div className="stat" key={index}>
            <span className="label">{performanceMap[key]}</span>
            <span className="value">{strategy[key]}</span>
          </div>
        );
      })
    : null;

  const strategyBio =
    strategy && !loading ? (
      showBio ? (
        <div className="sidebars">
          <StrategyBio strategy={strategy} setShowBio={setShowBio} />
          <div className="performance">
            <h2>Your Performance</h2>
            <div className="stats">{performance}</div>
          </div>
        </div>
      ) : (
        <div className="rates-bio toggle" onClick={() => setShowBio(!showBio)}>
          Expand Details
        </div>
      )
    ) : null;

  const searchBar =
    products && (products.length || productQuery) ? (
      <SearchBar
        setSearchQuery={setProductQuery}
        placeholder="Search for a Product"
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        loading={loading}
        xIcon={true}
        blueXIcon={true}
      />
    ) : null;

  const mocksButton = session.isAdminView ? (
    <button onClick={() => setMockData(!mockData)}>Mocks</button>
  ) : null;

  const availabilityDropdown = productVisibility ? (
    <div className="availability-dropdown">
      <RetailerOverviewDropdown
        type="availability"
        items={availability}
        updateOptions={setAvailability}
        gaEvent={{ category: 'Product Discovery', label: 'Availability Dropdown' }}
      />
    </div>
  ) : null;

  const reportDropdown = productVisibility ? (
    <Flag name="nrpFeatures">
      <ReportDropdown
        statType="product-list"
        orgId={session.orgId}
        gaEvent={{
          category: window.location.pathname,
          label: `Product Discovery Download CSV`,
        }}
        queryParams={params}
        reportOptionsOverride={reportOptionsOverride}
        rowCounts={rowCounts}
        strategy={strategy}
        disabled={loading}
      ></ReportDropdown>
    </Flag>
  ) : null;

  return (
    <div className="strategy-products-container fs-unmask">
      <div className="navigation">
        <Link to={`/v2/publisher/${session.orgId}/new-recs/rates`}>All Partners</Link>
        <img src={breadCrumbArrow} alt="arrow" className="breadcrumb-arrow" />
        <Link to={`/v2/publisher/${session.orgId}/new-recs/rates/${match.params.retailerId}`}>
          {strategy && strategy.merchant_name}
        </Link>
        <img src={breadCrumbArrow} alt="arrow" className="breadcrumb-arrow" />
        <span className="strategy-name">{strategy && strategy.strategy_name}</span>
        {mocksButton}
      </div>
      <div className={`content ${showBio ? 'collapse' : 'expand'}`}>
        {strategyBio}
        <div className="title-products">
          <h1>Products in this Strategy</h1>
          <div className="filter-items">
            <div className="search-bar">{searchBar}</div>
            <div className="dropdowns">
              {availabilityDropdown}
              <div className="sort-dropdown">{sortByDropdown}</div>
              {reportDropdown}
            </div>
          </div>
          <div className="products">
            <div className="table-container"></div>
            {productItems}
          </div>
        </div>
      </div>
      <Loader loading={loading} />
      <div className="footer">
        <Pagination
          showPagination={!loading && totalItems}
          currentPage={page}
          totalItems={products && totalItems}
          perPage={perPage}
          eventPrefix="Strategy Products"
          updatePage={setPage}
          updatePerPage={setPerPage}
        />
      </div>
    </div>
  );
};

export default StrategyProducts;
